import React from "react";
import classnames from "classnames";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useProductCardContext } from "./product-card";
import ProductCardLink from "./product-card-link";
import styles from "./product-card-details.css";

interface ProductCardDetailsProps {
    showVariant?: boolean;
}

const defaultProps = {
    showVariant: false,
};

function ProductCardDetails({
    showVariant,
}: ProductCardDetailsProps = defaultProps): React.ReactElement {
    const {
        detail_lead_enabled: detailLeadEnabled,
        designer_name: designerName,
        short_description: shortDescription,
        full_price_with_currency_symbol: fullPriceWithCurrencySymbol,
        has_variant: hasVariant,
        in_stock: inStock,
        show_retailer_as_affiliate: showRetailerAsAffiliate,
        retailer_name: retailerName,
    } = useProductCardContext();

    function handleShortDescriptionClick(): void {
        analytics.event("product_card", "short description");
    }

    function handleAffiliateRetailerClick(): void {
        analytics.event("product_card", "retailer");
    }

    return (
        <div className={styles.details}>
            <ProductCardLink
                reason="text"
                className={styles.detailsLink}
                predicate={detailLeadEnabled}
            >
                <div className={styles.designer}>{designerName}</div>

                {!!shortDescription && (
                    <div className={styles.shortDescription} onClick={handleShortDescriptionClick}>
                        {shortDescription}
                    </div>
                )}
            </ProductCardLink>

            <div className={styles.priceSection}>
                {fullPriceWithCurrencySymbol ? (
                    <ProductCardPriceSection />
                ) : (
                    <ProductCardNotAvailable />
                )}
            </div>

            {hasVariant && inStock && showVariant && (
                <p className={styles.variants}>
                    <em>{gettext("product_card.more_colors_available")}</em>
                </p>
            )}

            {showRetailerAsAffiliate && (
                <div className={styles.affiliateRetailer} onClick={handleAffiliateRetailerClick}>
                    <SVGIcon name="hyperlink" />
                    <span>{retailerName}</span>
                </div>
            )}
        </div>
    );
}

export function ProductCardPriceSection(): React.ReactElement {
    const {
        full_price_with_currency_symbol: fullPriceWithCurrencySymbol,
        sale_price_with_currency_symbol: salePriceWithCurrencySymbol,
        in_stock: inStock,
        free_shipping: freeShipping,
        on_promotion: onPromotion,
        sale_discount: saleDiscount,
        discount_info: discountInfo,
    } = useProductCardContext();

    if (!inStock) {
        return (
            <>
                <del className={styles.price}>{fullPriceWithCurrencySymbol}</del>

                <span className={styles.soldOutMessage}>{gettext("product_card.sold_out")}</span>
            </>
        );
    }
    return (
        <>
            {freeShipping && <SVGIcon className={styles.icon} name="truck" />}

            {onPromotion && <SVGIcon className={styles.icon} name="tag" />}

            {saleDiscount ? (
                <>
                    <del className={styles.price}>{fullPriceWithCurrencySymbol}</del>
                    <span className={classnames(styles.price, styles.salePrice)}>
                        {salePriceWithCurrencySymbol}
                    </span>
                </>
            ) : (
                <span className={styles.price}>{fullPriceWithCurrencySymbol}</span>
            )}

            {discountInfo && <span className={styles.discountInfo}>{discountInfo}</span>}
        </>
    );
}

export function ProductCardNotAvailable(): React.ReactElement {
    return (
        <span className={styles.soldOutMessage}>
            {gettext("product_card.not_available_in_your_country")}
        </span>
    );
}

export default ProductCardDetails;
